/**
 * SetPasswordPage.jsx
 * Page for setting password after email verification.
 * Accessed via verification link from email.
 */

import React, { useState, useEffect, useMemo } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Eye, EyeOff, Lock, CheckCircle, XCircle, AlertCircle, Loader2, User } from 'lucide-react';
import { API_ENDPOINTS } from '../../../config/api';

// Channel name for account setup notification
const ACCOUNT_SETUP_CHANNEL = 'banyan-account-setup';

// M4: Tell the browser not to send a Referer header (which would leak the
// token to any third-party asset embedded on this page) when this page is
// the source of any outgoing request.
const useNoReferrerMeta = () => {
  useEffect(() => {
    const existing = document.querySelector('meta[name="referrer"]');
    const previous = existing?.getAttribute('content') ?? null;
    let element = existing;
    if (!element) {
      element = document.createElement('meta');
      element.setAttribute('name', 'referrer');
      document.head.appendChild(element);
    }
    element.setAttribute('content', 'no-referrer');
    return () => {
      if (previous === null) {
        element.parentNode?.removeChild(element);
      } else {
        element.setAttribute('content', previous);
      }
    };
  }, []);
};

/**
 * Password requirement checker
 */
const usePasswordValidation = (password) => {
  return useMemo(() => {
    return {
      minLength: password.length >= 9,
      hasUppercase: /[A-Z]/.test(password),
      hasLowercase: /[a-z]/.test(password),
      hasNumber: /\d/.test(password),
      hasSpecial: /[!@#$%^&*(),.?":{}|<>]/.test(password),
    };
  }, [password]);
};

/**
 * Password requirement indicator component
 */
const PasswordRequirement = ({ met, label }) => (
  <div className={`flex items-center gap-2 text-sm ${met ? 'text-green-600' : 'text-gray-500'}`}>
    {met ? (
      <CheckCircle className="w-4 h-4" />
    ) : (
      <XCircle className="w-4 h-4 text-gray-400" />
    )}
    <span>{label}</span>
  </div>
);

/**
 * SetPasswordPage Component
 * Handles password setup for new accounts.
 */
const SetPasswordPage = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();

  const token = searchParams.get('token');

  // M4: apply <meta name="referrer" content="no-referrer">.
  useNoReferrerMeta();

  // M4: scrub the token from the visible URL + browser history as soon as
  // we have it. The component captured the token from `useSearchParams`
  // above, so we no longer need it in the address bar.
  useEffect(() => {
    if (!token) return;
    try {
      window.history.replaceState({}, '', window.location.pathname);
    } catch (_) {
      // history API may be unavailable in unusual contexts; safe to ignore.
    }
  }, [token]);

  // State
  const [isVerifying, setIsVerifying] = useState(true);
  const [isValid, setIsValid] = useState(false);
  const [email, setEmail] = useState('');
  const [tokenError, setTokenError] = useState('');
  
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState('');
  const [submitSuccess, setSubmitSuccess] = useState(false);
  const [countdown, setCountdown] = useState(5);
  
  // Username validation
  const usernameValid = username.trim().length >= 5;
  
  // Password validation
  const passwordChecks = usePasswordValidation(password);
  const allRequirementsMet = Object.values(passwordChecks).every(Boolean);
  const passwordsMatch = password === confirmPassword && confirmPassword.length > 0;
  
  // Countdown timer for success state
  useEffect(() => {
    if (!submitSuccess) return;
    
    if (countdown > 0) {
      const timer = setTimeout(() => {
        setCountdown(countdown - 1);
      }, 1000);
      return () => clearTimeout(timer);
    } else {
      // Try to close tab when countdown reaches 0
      window.close();
      // Fallback: redirect to login if window.close() doesn't work
      setTimeout(() => {
        navigate('/login');
      }, 500);
    }
  }, [submitSuccess, countdown, navigate]);

  // Verify token on mount
  useEffect(() => {
    const verifyToken = async () => {
      if (!token) {
        setTokenError('No verification token provided');
        setIsVerifying(false);
        return;
      }
      
      try {
        // M4: send the token in a JSON body via POST so it does not appear
        // in browser history, the `Referer` header, or access logs.
        const response = await fetch(API_ENDPOINTS.auth.verifyEmailToken, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          referrerPolicy: 'no-referrer',
          body: JSON.stringify({ token }),
        });
        const data = await response.json();
        
        if (data.valid) {
          setIsValid(true);
          setEmail(data.email);
        } else {
          setTokenError(data.message || 'Invalid or expired verification link');
        }
      } catch (err) {
        setTokenError('Failed to verify token. Please try again.');
      } finally {
        setIsVerifying(false);
      }
    };
    
    verifyToken();
  }, [token]);
  
  // Handle form submission
  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!usernameValid) {
      setSubmitError('Username must be at least 5 characters');
      return;
    }
    
    if (!allRequirementsMet) {
      setSubmitError('Please meet all password requirements');
      return;
    }
    
    if (!passwordsMatch) {
      setSubmitError('Passwords do not match');
      return;
    }
    
    setIsSubmitting(true);
    setSubmitError('');
    
    try {
      const response = await fetch(API_ENDPOINTS.auth.setPassword, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        referrerPolicy: 'no-referrer',
        body: JSON.stringify({
          token,
          username: username.trim(),
          password,
          confirm_password: confirmPassword,
        }),
      });
      
      const data = await response.json();
      
      if (response.ok) {
        setSubmitSuccess(true);
        
        // Notify original tab via BroadcastChannel
        const channel = new BroadcastChannel(ACCOUNT_SETUP_CHANNEL);
        channel.postMessage({
          type: 'ACCOUNT_SETUP_COMPLETE',
          email: email,
        });
        channel.close();
      } else {
        setSubmitError(data.detail || 'Failed to set password');
      }
    } catch (err) {
      setSubmitError('Network error. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };
  
  // Loading state
  if (isVerifying) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900">
        <div className="text-center">
          <Loader2 className="w-12 h-12 text-blue-500 animate-spin mx-auto mb-4" />
          <p className="text-slate-400">Verifying your link...</p>
        </div>
      </div>
    );
  }
  
  // Invalid token state
  if (!isValid) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900">
        <div className="bg-white rounded-lg shadow-xl p-8 max-w-md w-full mx-4 text-center">
          <AlertCircle className="w-16 h-16 text-red-500 mx-auto mb-4" />
          <h2 className="text-2xl font-semibold text-gray-900 mb-2">Invalid Link</h2>
          <p className="text-gray-600 mb-6">{tokenError}</p>
          <button
            onClick={() => navigate('/login')}
            className="bg-gradient-to-r from-blue-500 to-purple-600 text-white px-6 py-3 rounded-lg font-medium
              hover:from-blue-600 hover:to-purple-700 transition-all"
          >
            Go to Sign In
          </button>
        </div>
      </div>
    );
  }
  
  // Success state
  if (submitSuccess) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900">
        <div className="bg-white rounded-lg shadow-xl p-8 max-w-md w-full mx-4 text-center">
          <CheckCircle className="w-16 h-16 text-green-500 mx-auto mb-4" />
          <h2 className="text-2xl font-semibold text-gray-900 mb-2">Account Created Successfully!</h2>
          <p className="text-gray-600 mb-4">Your account is ready. You can now sign in.</p>
          <p className="text-gray-500 mb-6">
            Return to your previous tab. This window will close in{' '}
            <span className="font-bold text-blue-600">{countdown}</span> second{countdown !== 1 ? 's' : ''}...
          </p>
          <button
            onClick={() => navigate('/login')}
            className="bg-gradient-to-r from-blue-500 to-purple-600 text-white px-6 py-3 rounded-lg font-medium
              hover:from-blue-600 hover:to-purple-700 transition-all"
          >
            Go to Sign In
          </button>
        </div>
      </div>
    );
  }
  
  return (
    <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900 p-4">
      {/* Background decoration */}
      <div className="absolute inset-0 overflow-hidden">
        <div className="absolute top-1/4 left-1/4 w-[500px] h-[500px] bg-blue-500/10 rounded-full blur-3xl"></div>
        <div className="absolute bottom-1/4 right-1/4 w-[500px] h-[500px] bg-purple-500/10 rounded-full blur-3xl"></div>
      </div>
      
      <div className="bg-white rounded-lg shadow-xl p-8 max-w-md w-full relative z-10">
        {/* Header */}
        <div className="text-center mb-6">
          <h1 className="text-2xl font-semibold text-gray-900 mb-2">
            Set Your Password
          </h1>
          <p className="text-gray-600 text-sm">
            Create a password for <span className="font-medium">{email}</span>
          </p>
        </div>
        
        {/* Error message */}
        {submitError && (
          <div className="mb-4 p-3 border rounded-lg bg-red-50 border-red-200">
            <p className="text-sm text-red-600">{submitError}</p>
          </div>
        )}
        
        {/* Form */}
        <form onSubmit={handleSubmit} className="space-y-4">
          {/* Username field */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Username
            </label>
            <div className="relative">
              <User className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
              <input
                type="text"
                value={username}
                onChange={(e) => setUsername(e.target.value)}
                className={`w-full pl-10 pr-4 py-3 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all text-gray-900 ${
                  username && !usernameValid ? 'border-red-300' : 'border-gray-300'
                }`}
                placeholder="Choose a username (min. 5 characters)"
                disabled={isSubmitting}
              />
            </div>
            {username && !usernameValid && (
              <p className="text-sm text-red-600 mt-1">Username must be at least 5 characters</p>
            )}
            {usernameValid && (
              <p className="text-sm text-green-600 mt-1 flex items-center gap-1">
                <CheckCircle className="w-4 h-4" /> Username is valid
              </p>
            )}
          </div>
          
          {/* Password field */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Password
            </label>
            <div className="relative">
              <Lock className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
              <input
                type={showPassword ? 'text' : 'password'}
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                className="w-full pl-10 pr-12 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all text-gray-900"
                placeholder="Create a strong password"
                disabled={isSubmitting}
              />
              <button
                type="button"
                onClick={() => setShowPassword(!showPassword)}
                className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600"
              >
                {showPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
              </button>
            </div>
          </div>
          
          {/* Password requirements */}
          <div className="bg-gray-50 rounded-lg p-4 space-y-2">
            <p className="text-sm font-medium text-gray-700 mb-2">Password requirements:</p>
            <PasswordRequirement met={passwordChecks.minLength} label="At least 9 characters" />
            <PasswordRequirement met={passwordChecks.hasUppercase} label="One uppercase letter" />
            <PasswordRequirement met={passwordChecks.hasLowercase} label="One lowercase letter" />
            <PasswordRequirement met={passwordChecks.hasNumber} label="One number" />
            <PasswordRequirement met={passwordChecks.hasSpecial} label="One special character (!@#$%^&*)" />
          </div>
          
          {/* Confirm password field */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Confirm Password
            </label>
            <div className="relative">
              <Lock className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
              <input
                type={showConfirmPassword ? 'text' : 'password'}
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
                className={`w-full pl-10 pr-12 py-3 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all text-gray-900 ${
                  confirmPassword && !passwordsMatch ? 'border-red-300' : 'border-gray-300'
                }`}
                placeholder="Confirm your password"
                disabled={isSubmitting}
              />
              <button
                type="button"
                onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600"
              >
                {showConfirmPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
              </button>
            </div>
            {confirmPassword && !passwordsMatch && (
              <p className="text-sm text-red-600 mt-1">Passwords do not match</p>
            )}
            {passwordsMatch && (
              <p className="text-sm text-green-600 mt-1 flex items-center gap-1">
                <CheckCircle className="w-4 h-4" /> Passwords match
              </p>
            )}
          </div>
          
          {/* Submit button */}
          <button
            type="submit"
            disabled={isSubmitting || !usernameValid || !allRequirementsMet || !passwordsMatch}
            className="w-full bg-gradient-to-r from-blue-500 to-purple-600 text-white py-3 rounded-lg font-medium
              hover:from-blue-600 hover:to-purple-700 transition-all disabled:opacity-50 disabled:cursor-not-allowed
              flex items-center justify-center gap-2"
          >
            {isSubmitting ? (
              <>
                <Loader2 className="w-5 h-5 animate-spin" />
                Setting Password...
              </>
            ) : (
              'Set Password'
            )}
          </button>
        </form>
      </div>
    </div>
  );
};

export default SetPasswordPage;
